// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { OutwriteShadesBrandColor } from '@outwrite/themes'
import type { TFunction } from 'react-i18next'
import type { I18nextKey, ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

import styled from '@emotion/styled'

// Others
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'
import AcknowledgementOfCountry from '../components/localized/AcknowledgementOfCountry'

import {
  HeroSection,
  Title as HeroTitle,
  Description as HeroDescription
} from '../components/Hero'
import { Section, Description, Item, FlexItem, descriptionCss } from '../components/Section'
import { AltFlexSectionEven, AltFlexSectionOdd, Title as AltTitle } from '../components/AltSection'

import PrimaryAction from '../components/PrimaryAction'
import Highlight from '../components/Highlight'
import BottomCta from '../components/BottomCta'

import { defaultPageSpecificSEO, pageSpecificCtaLink } from '../constants'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'careers' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, careers: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants & Styles
 */

const NoMarginDescription = styled.p`
  ${descriptionCss}
  margin-bottom: 0;
`

const Ul = styled.ul`
  ${descriptionCss}
  margin: 0;
`

const Li = styled.li`
  &::marker {
    font-size: 0.7em;
  }
`

/**
 * Types for structure of page
 */

// did this manually for each key because it was a pain to sort out typescript issues
// but it would be better if we just type it so its just the prefix of the key needed
type CareersKey = I18nextKey<typeof import ('../../public/locales/en/careers.json')>
interface FlexSectionContent {
  titleKey: CareersKey
  description: { key?: CareersKey, component?: React.FC<{ t: TFunction<'careers', undefined> }> }
  img: { src: any } & Record<'altKey', CareersKey>
  color: keyof OutwriteShadesBrandColor
}

/**
 * Mapping json keys for content structure
 */

const SECTIONS: FlexSectionContent[] = [
  {
    titleKey: 'sections.who.TITLE',
    description: {
      key: 'sections.who.DESCRIPTION'
    },
    img: {
      src: require('../../public/assets/team-photo.jpg'),
      altKey: 'sections.who.TEAM_IMG_ALT'
    },
    color: 'purple'
  },
  {
    titleKey: 'sections.what.TITLE',
    description: {
      key: 'sections.what.DESCRIPTION'
    },
    img: {
      src: require('../../public/assets/feature-images/en/rewrite-formal.svg'),
      altKey: 'sections.what.FORMAL_POPUP_IMG_ALT'
    },
    color: 'yellow'
  },
  {
    titleKey: 'sections.benefits.TITLE',
    description: {
      component: ({ t }): JSX.Element => (
        <>
          <NoMarginDescription>{t('sections.benefits.DESCRIPTION')}</NoMarginDescription>
          {/*
            // for some reason cannot use the 'components' property of <Trans /> for this
            // so just doing it this way
          */}
          <Trans
            t={t}
            i18nKey='sections.benefits.DESCRIPTION_BENEFITS'
            defaults='<0><1>Flexible work arrangements</1><1>Professional development budget</1><1>ESOP</1></0>'
          ><Ul><Li></Li><Li></Li></Ul></Trans>
        </>
      )
    },
    img: {
      src: require('../../public/assets/team-golf-photo.png'),
      altKey: 'sections.benefits.TEAM_GOLF_IMG_ALT'
    },
    color: 'purple'
  }
]

const Careers: NextPage = () => {
  const router = useRouter()
  const { t } = useTranslation('careers')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      <Header t={tCommon} action={ctaAction} data-sticky={scrolledPassed} />

      {/* Hero */}
      <HeroSection>
        <HeroTitle>
          <Trans
            t={t}
            i18nKey={'hero.TITLE'}
            components={[<Highlight color='yellow' key={0} />, <Highlight color='purple' key={1} />]}
          />
        </HeroTitle>
        <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
        <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
      </HeroSection>

      <Section bgColor='gray'>
        {/* Sections */}
        {SECTIONS.map(({
          img,
          titleKey,
          description: { key: descriptionKey, component: DescriptionComponent },
          color
        }, i) => {
          const Wrapper = i % 2 ? AltFlexSectionEven : AltFlexSectionOdd
          return (
            <Wrapper
              key={i}
              bgColor={color}
            >
              <Item>
                <Image
                  src={img.src}
                  alt={t(img.altKey) as string}
                  height={312}
                  width={467}
                />
              </Item>
              <FlexItem>
                <AltTitle>{t(titleKey)}</AltTitle>
                {descriptionKey
                  ? <Description>{t(descriptionKey)}</Description>
                  : DescriptionComponent && <DescriptionComponent t={t} />
                }
              </FlexItem>
            </Wrapper>
          )
        })}
      </Section>

      <AcknowledgementOfCountry t={tCommon} />

      {/* BottomCta */}
      <BottomCta
        action={ctaAction}
        {...t('BOTTOM_CTA')}
        bgColor='gray'
      />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Careers
